import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import * as css from './SeatmapManage.css';
import {ARMCHAIR_ICON, HANDSHAKE_ICON, EYE_SLASH_ICON, FLAME_ICON, SOLD_FILL, HIDDEN_FILL, SELECTED_BG, SELECT_STROKE, BURNED_FILL} from './seatIcons';

// Seat fill — LEGACY rule: a free seat is painted in its (first) TICKET's
// color; the map's category color is only a fallback for unassigned seats.
const seatFill = (s, ticketColors) => {
    if(s.state === 'sold') return SOLD_FILL;
    if(s.state === 'hidden') return HIDDEN_FILL;
    const t = (s.tids || []).length ? ticketColors.get(s.tids[0]) : null;
    return t || s.color || '#93c5fd';
};
// all DISTINCT ticket colors on a seat (assignment order, capped at 4 — thinner
// wedges stop reading as colors); 2+ renders as pie wedges (replaced the old
// second-ticket outer ring). Sold/hidden keep their single state fill.
const seatColorList = (s, ticketColors) => {
    if(s.state === 'sold' || s.state === 'hidden') return [seatFill(s, ticketColors)];
    const cols = [];
    for(const tid of (s.tids || [])) {
        const c = ticketColors.get(tid);
        if(c && !cols.includes(c)) cols.push(c);
        if(cols.length >= 4) break;
    }
    return cols.length ? cols : [s.color || '#93c5fd'];
};

// pie-slice path from angle a0 to a1 (radians, 12 o'clock = -PI/2, clockwise)
const wedgePath = (cx, cy, r, a0, a1) => {
    const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    const large = a1 - a0 > Math.PI ? 1 : 0;
    return `M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} Z`;
};

// seat-number color by the seat's EFFECTIVE luminance — white numbers were
// unreadable on light ticket colors (and on the washed-out blocked seats).
const numColor = (s, ticketColors) => {
    const hex = String(seatFill(s, ticketColors)).replace('#', '');
    let lum = 0.55;
    if(hex.length >= 6) {
        const rr = parseInt(hex.slice(0, 2), 16), gg = parseInt(hex.slice(2, 4), 16), bb = parseInt(hex.slice(4, 6), 16);
        lum = (0.299 * rr + 0.587 * gg + 0.114 * bb) / 255;
    }
    if(s.state === 'blocked') lum = lum * 0.33 + 0.97 * 0.67; // 0.33 fill over the light page
    return lum > 0.58 ? '#1e293b' : '#ffffff';
};

const selectable = (s) => s.state !== 'sold';

// Base seat layer — 8k+ circles (matches the buyer form widget). Rendered ONCE (memo on
// dataVersion); pan/zoom only change the <svg> viewBox, selection is an overlay.
const SeatLayer = memo(function SeatLayer({seats, r, ticketColors}) {
    return (
        <g>
            {seats.map((s) => {
                const cols = seatColorList(s, ticketColors);
                const op = s.state === 'blocked' ? 0.33 : 1;
                const step = (2 * Math.PI) / cols.length;
                return (
                    <g key={s.code}>
                        {/* base circle keeps the data-code hit target; wedges for
                            colors 2..n overlay their slices (pointer-transparent) */}
                        <circle data-code={s.code} cx={s.x} cy={s.y} r={r * 1.1}
                            fill={seatFill(s, ticketColors)}
                            fillOpacity={op} />
                        {cols.length > 1 && cols.slice(1).map((col, i) => (
                            <path key={i} data-wedge={s.code} pointerEvents="none" fill={col} fillOpacity={op}
                                d={wedgePath(s.x, s.y, r * 1.1, -Math.PI / 2 + (i + 1) * step, -Math.PI / 2 + (i + 2) * step)} />
                        ))}
                    </g>
                );
            })}
        </g>
    );
}, (a, b) => a.dataVersion === b.dataVersion && a.r === b.r);

// State-icon layer (sold armchair / burned flame / reserved handshake /
// blocked eye) — the legacy glyphs. Rendered ONCE per data change (memo);
// pan/zoom move it via the viewBox. `badge` flips the icon from seat-centred
// to a corner badge when seat numbers are visible (numbers ALWAYS show —
// the one deliberate divergence from legacy).
const StateIconLayer = memo(function StateIconLayer({stateSeats, r, badge}) {
    return (
        <g pointerEvents="none">
            {stateSeats.map((s) => {
                const href = s.state === 'sold' ? (s.enter ? FLAME_ICON : ARMCHAIR_ICON)
                    : s.state === 'reserved' ? HANDSHAKE_ICON
                        : s.state === 'blocked' ? EYE_SLASH_ICON : null;
                if(!href) return null;
                const burned = s.state === 'sold' && !!s.enter;
                const sz = badge ? r * 1.25 : r * 1.35;
                const ccx = badge ? s.x + r * 0.95 : s.x;
                const ccy = badge ? s.y - r * 0.95 : s.y;
                const imSz = burned ? sz * 0.62 : sz * 0.8;
                return (
                    <g key={'ic' + s.code}>
                        {/* dark disc behind the glyph — the legacy icons are WHITE, so a
                            contrasting badge is what makes them actually visible */}
                        <circle cx={ccx} cy={ccy} r={sz / 2}
                            fill={burned ? BURNED_FILL : '#334155'}
                            stroke="#ffffff" strokeWidth={r * 0.08} />
                        <image href={href} x={ccx - imSz / 2} y={ccy - imSz / 2} width={imSz} height={imSz}
                            preserveAspectRatio="xMidYMid meet" />
                    </g>
                );
            })}
        </g>
    );
}, (a, b) => a.dataVersion === b.dataVersion && a.r === b.r && a.badge === b.badge);

function SeatCanvas({seats, bounds, dataVersion, selected, setSelected, ticketColors, highlightCodes, areas, onSelectArea, onHoverArea, onSoldClick, onReservedClick, focusArea}) {
    const svgRef = useRef(null);
    const wrapRef = useRef(null);

    // FORM PARITY: the buyer widget (PickerCanvas SEAT_R) draws every seat at a
    // FIXED 8.5 world-unit radius over the same projection coordinates — the
    // manager must render the exact same circle size and seat-to-seat gaps.
    // The seat circle below draws at r*1.1, so base r = 8.5/1.1.
    const r = 8.5 / 1.1;

    const areaTitle = useMemo(() => {
        const m = {}; (areas || []).forEach((a) => { m[a.smaid] = a.title; }); return m;
    }, [areas]);
    const centroids = useMemo(() => {
        const acc = {};
        for(const s of seats) {
            const a = acc[s.smaid] || (acc[s.smaid] = {sx: 0, sy: 0, n: 0});
            a.sx += s.x; a.sy += s.y; a.n++;
        }
        return Object.entries(acc).map(([smaid, a]) => ({smaid: Number(smaid), x: a.sx / a.n, y: a.sy / a.n}));
    }, [seats]);

    // seats per DISPLAY row, sorted along the row's dominant axis — used by
    // row-label click (whole-row select) and the dblclick contiguous-run walk.
    // Label POSITIONS come from the widget-parity pass below, not from here.
    const rowGroups = useMemo(() => {
        const g = {};
        for(const s of seats) {
            if(s.row == null || s.row === '') continue;
            const k = s.smaid + '|' + s.row;
            (g[k] = g[k] || {key: k, smaid: s.smaid, row: s.row, seats: []}).seats.push(s);
        }
        const out = Object.values(g);
        for(const e of out) {
            const xs = e.seats.map((s) => s.x), ys = e.seats.map((s) => s.y);
            const horizontal = (Math.max(...xs) - Math.min(...xs)) >= (Math.max(...ys) - Math.min(...ys));
            e.seats.sort((a, b) => (horizontal ? a.x - b.x : a.y - b.y));
        }
        return out;
    }, [seats]);

    const pad = r * 6;
    const initialVB = useMemo(() => ({
        x: bounds.minX - pad, y: bounds.minY - pad,
        w: (bounds.maxX - bounds.minX) + pad * 2, h: (bounds.maxY - bounds.minY) + pad * 2
    }), [bounds, pad]);
    const [vb, setVb] = useState(initialVB);
    // re-fit ONLY when the map's real extent changes — the background data
    // resync after every action returns a new bounds OBJECT with the same
    // values, and resetting on identity yanked the user's zoom away.
    const boundsKey = `${bounds.minX}|${bounds.minY}|${bounds.maxX}|${bounds.maxY}|${pad}`;
    useEffect(() => {
        setVb({
            x: bounds.minX - pad, y: bounds.minY - pad,
            w: (bounds.maxX - bounds.minX) + pad * 2, h: (bounds.maxY - bounds.minY) + pad * 2
        });
    }, [boundsKey]); // eslint-disable-line

    const seatByCode = useMemo(() => {
        const m = new Map(); seats.forEach((s) => m.set(s.code, s)); return m;
    }, [seats]);

    const [svgW, setSvgW] = useState(1000);
    useEffect(() => {
        const el = svgRef.current;
        if(!el || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(() => setSvgW(el.clientWidth || 1000));
        ro.observe(el); setSvgW(el.clientWidth || 1000);
        return () => ro.disconnect();
    }, []);
    const screenR = r * (svgW / vb.w);
    const showSeatNums = screenR >= 8;

    // in-view seats for the NUMBER layer, capped.
    const visibleSeats = useMemo(() => {
        const mx = vb.w * 0.03, my = vb.h * 0.03;
        const x0 = vb.x - mx, x1 = vb.x + vb.w + mx, y0 = vb.y - my, y1 = vb.y + vb.h + my;
        const out = [];
        for(const s of seats) {
            if(s.x >= x0 && s.x <= x1 && s.y >= y0 && s.y <= y1) { out.push(s); if(out.length > 3000) break; }
        }
        return out;
    }, [vb, seats]);
    // state-icon seats get their OWN memoized layer so a reserved/sold seat is
    // NEVER dropped by the number cap — the שיריון is visible at any zoom.
    const stateSeats = useMemo(() => seats.filter((s) => s.state === 'sold' || s.state === 'reserved' || s.state === 'blocked'), [seats]);

    // Row-number label positions — EXACT port of the buyer widget's rowEnds
    // pass (seatmap-studio PickerCanvas.tsx) so the manager's row numbers sit
    // precisely where the form's do: per-TRUE-row (seat-code prefix, not the
    // display rowLabel — two blocks can share a rowLabel) end labels one
    // seat-step past the ends along the LOCAL direction, labels at aisle gaps
    // (narrow gap = centered, wide gap = one per side), cross-block merge in a
    // shared aisle, then a collision pass that nudges a label off any seat
    // along its escape direction (or drops it). Viewport-independent.
    const rowLabels = useMemo(() => {
        const SEAT_R = 8.5; // widget world seat radius — all formulas below are in its units
        const rowIdOf = (code) => code.slice(0, code.lastIndexOf('#'));
        const slotOf = (code) => Number(code.slice(code.lastIndexOf('#') + 1));
        const byRowId = new Map();
        for(const s of seats) {
            if(s.row == null || s.row === '') continue;
            const k = rowIdOf(s.code);
            const arr = byRowId.get(k);
            if(arr) arr.push(s); else byRowId.set(k, [s]);
        }
        // GLOBAL seat pitch (median consecutive-slot distance over every row):
        // the step for sparse rows — a 2-seat row must not take its own gap as
        // the pitch and throw its labels onto another row's seats.
        const allDists = [];
        for(const ss of byRowId.values()) {
            const sorted = [...ss].sort((a, b) => slotOf(a.code) - slotOf(b.code));
            for(let i = 1; i < sorted.length; i++) allDists.push(Math.hypot(sorted[i].x - sorted[i - 1].x, sorted[i].y - sorted[i - 1].y));
        }
        const globalPitch = allDists.length ? [...allDists].sort((a, b) => a - b)[Math.floor(allDists.length / 2)] : SEAT_R * 2.4;
        const out = [];
        for(const ss of byRowId.values()) {
            const row = ss[0].row;
            const rowKey = ss[0].smaid + '|' + row; // click target stays the DISPLAY row
            const pts = [...ss].sort((a, b) => slotOf(a.code) - slotOf(b.code));
            const n = pts.length;
            if(n === 1) {
                const step = Math.max(globalPitch, SEAT_R * 2.4);
                out.push({x: pts[0].x - step, y: pts[0].y, row, rowKey, px: -1, py: 0});
                out.push({x: pts[0].x + step, y: pts[0].y, row, rowKey, px: 1, py: 0});
                continue;
            }
            const dists = [];
            for(let i = 1; i < n; i++) dists.push(Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y));
            const medRaw = [...dists].sort((a, b) => a - b)[Math.floor(dists.length / 2)] || SEAT_R * 2.4;
            const med = n >= 4 ? Math.min(medRaw, globalPitch * 1.6) : Math.min(medRaw, globalPitch * 1.1);
            // LOCAL directions at each end — curved/fanned rows keep their line
            const dirOf = (i, j) => {
                const dx = pts[j].x - pts[i].x, dy = pts[j].y - pts[i].y;
                const len = Math.hypot(dx, dy) || 1;
                return {ux: dx / len, uy: dy / len};
            };
            const sd = dirOf(0, Math.min(2, n - 1));
            const ed = dirOf(Math.max(0, n - 3), n - 1);
            out.push({x: pts[0].x - sd.ux * med, y: pts[0].y - sd.uy * med, row, rowKey, px: -sd.ux, py: -sd.uy});
            out.push({x: pts[n - 1].x + ed.ux * med, y: pts[n - 1].y + ed.uy * med, row, rowKey, px: ed.ux, py: ed.uy});
            // aisle gaps: consecutive-by-slot distance well above the GLOBAL
            // pitch. Narrow (≤ ~2 missing seats) → one centered label; wider →
            // one on each side, a seat-step past the bounding seats.
            for(let i = 1; i < n; i++) {
                if(dists[i - 1] > globalPitch * 1.7) {
                    const d = dirOf(i - 1, i);
                    if(dists[i - 1] <= globalPitch * 2.8) {
                        out.push({x: (pts[i - 1].x + pts[i].x) / 2, y: (pts[i - 1].y + pts[i].y) / 2, row, rowKey, px: 0, py: 0});
                    } else {
                        out.push({x: pts[i - 1].x + d.ux * med, y: pts[i - 1].y + d.uy * med, row, rowKey, px: d.ux, py: d.uy});
                        out.push({x: pts[i].x - d.ux * med, y: pts[i].y - d.uy * med, row, rowKey, px: -d.ux, py: -d.uy});
                    }
                }
            }
        }
        // a row often CONTINUES across blocks/sections — merge same-row labels
        // that land close together in the shared aisle into one, centered
        const merged = [];
        for(const e of out) {
            const clash = merged.find((d) => d.row === e.row && Math.hypot(d.x - e.x, d.y - e.y) < SEAT_R * 4.5);
            if(clash) { clash.x = (clash.x + e.x) / 2; clash.y = (clash.y + e.y) / 2; continue; }
            merged.push(e);
        }
        // COLLISION PASS: a label must never sit on a seat — nudge outward
        // along its escape direction, else drop it
        const CELL = SEAT_R * 4;
        const grid = new Map();
        for(const s of seats) {
            const k = Math.floor(s.x / CELL) + ':' + Math.floor(s.y / CELL);
            const arr = grid.get(k);
            if(arr) arr.push(s); else grid.set(k, [s]);
        }
        const hitsSeat = (x, y) => {
            const cx = Math.floor(x / CELL), cy = Math.floor(y / CELL);
            for(let gx = cx - 1; gx <= cx + 1; gx++) for(let gy = cy - 1; gy <= cy + 1; gy++) {
                const cell = grid.get(gx + ':' + gy);
                if(!cell) continue;
                for(const p of cell) if(Math.hypot(p.x - x, p.y - y) < SEAT_R * 1.8) return true;
            }
            return false;
        };
        const placed = [];
        for(const e of merged) {
            if(!hitsSeat(e.x, e.y)) { placed.push(e); continue; }
            if(!e.px && !e.py) continue; // gap-center label with no escape route
            let ok = false;
            for(let step = 1; step <= 3 && !ok; step++) {
                const nx = e.x + e.px * SEAT_R * 1.4 * step;
                const ny = e.y + e.py * SEAT_R * 1.4 * step;
                if(!hitsSeat(nx, ny)) { e.x = nx; e.y = ny; ok = true; }
            }
            if(ok) placed.push(e);
        }
        return placed.map((e, i) => ({...e, key: 'rl' + i}));
    }, [seats]);

    // e2e hook: parity tests read the computed label positions (widget keeps
    // __tkPicker for the same purpose)
    useEffect(() => { window.__smapRowLabels = rowLabels; return () => { delete window.__smapRowLabels; }; }, [rowLabels]);

    // viewport culling only — positions themselves never depend on the viewport
    const rowLabelPts = useMemo(() => {
        const mx = vb.w * 0.03, my = vb.h * 0.03;
        return rowLabels.filter((l) => l.x >= vb.x - mx && l.x <= vb.x + vb.w + mx && l.y >= vb.y - my && l.y <= vb.y + vb.h + my);
    }, [rowLabels, vb]);
    // widget LOD + font parity: row numbers appear from the same zoom and keep
    // the same minimum on-screen size while the camera is far
    const scale = svgW / vb.w; // ≈ widget zoom (screen px per world unit)
    const showRowLabels = scale >= 0.48; // LOD_ROW_LABELS
    const rowFont = Math.min(8.5 * 1.9, Math.max(8.5 * 1.05, 10.5 / scale));

    // clicking a row number selects the whole row (legacy) — replace, Shift adds
    const selectRowByKey = (rowKey, additive) => {
        const g = rowGroups.find((x) => x.key === rowKey);
        if(!g) return;
        const codes = g.seats.filter(selectable).map((s) => s.code);
        setSelected((prev) => {
            const next = additive ? new Set(prev) : new Set();
            codes.forEach((c) => next.add(c));
            return next;
        });
    };

    const toWorld = useCallback((clientX, clientY) => {
        const rect = svgRef.current.getBoundingClientRect();
        const scale = Math.min(rect.width / vb.w, rect.height / vb.h);
        const offX = (rect.width - vb.w * scale) / 2;
        const offY = (rect.height - vb.h * scale) / 2;
        return {
            x: vb.x + (clientX - rect.left - offX) / scale,
            y: vb.y + (clientY - rect.top - offY) / scale
        };
    }, [vb]);

    // ---- hover-focus a gush (the sidebar אזורים list): tween the viewBox to
    // fit its seats with generous context padding; restore the exact previous
    // view when the hover ends. A user gesture (wheel/drag) cancels the tween.
    const focusTween = useRef(null);
    const focusPrevVb = useRef(null);
    const cancelFocusTween = useCallback(() => {
        if(focusTween.current) { cancelAnimationFrame(focusTween.current); focusTween.current = null; }
    }, []);
    const vbRef = useRef(vb);
    vbRef.current = vb;
    const tweenVb = useCallback((to, ms = 260) => {
        cancelFocusTween();
        const from = vbRef.current;
        const t0 = performance.now();
        const ease = (t) => 1 - Math.pow(1 - t, 3);
        const step = (now) => {
            const t = Math.min(1, (now - t0) / ms);
            const k = ease(t);
            setVb({
                x: from.x + (to.x - from.x) * k, y: from.y + (to.y - from.y) * k,
                w: from.w + (to.w - from.w) * k, h: from.h + (to.h - from.h) * k
            });
            focusTween.current = t < 1 ? requestAnimationFrame(step) : null;
        };
        focusTween.current = requestAnimationFrame(step);
    }, [cancelFocusTween]);
    useEffect(() => {
        if(focusArea != null) {
            const pts = seats.filter((s) => s.smaid === focusArea);
            if(!pts.length) return;
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            for(const s of pts) {
                if(s.x < minX) minX = s.x; if(s.x > maxX) maxX = s.x;
                if(s.y < minY) minY = s.y; if(s.y > maxY) maxY = s.y;
            }
            // a bit zoomed OUT of a tight fit — the gush stays in context
            const padX = Math.max((maxX - minX) * 0.45, r * 12);
            const padY = Math.max((maxY - minY) * 0.45, r * 12);
            if(!focusPrevVb.current) focusPrevVb.current = vb;
            tweenVb({x: minX - padX, y: minY - padY, w: (maxX - minX) + padX * 2, h: (maxY - minY) + padY * 2});
        } else if(focusPrevVb.current) {
            tweenVb(focusPrevVb.current);
            focusPrevVb.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusArea]);

    // ---- zoom (wheel / pinch / slider) ----
    const minW = r * 20;                 // max zoom-in
    const maxW = initialVB.w * 3;        // max zoom-out
    const zoomAt = useCallback((p, factor) => {
        cancelFocusTween(); focusPrevVb.current = null; // user took over the camera
        setVb((v) => {
            const nw = Math.min(maxW, Math.max(minW, v.w * factor));
            const nh = nw * (v.h / v.w);
            return {x: p.x - (p.x - v.x) * (nw / v.w), y: p.y - (p.y - v.y) * (nh / v.h), w: nw, h: nh};
        });
    }, [minW, maxW, cancelFocusTween]);
    const onWheel = useCallback((e) => {
        e.preventDefault();
        zoomAt(toWorld(e.clientX, e.clientY), e.deltaY > 0 ? 1.12 : 1 / 1.12);
    }, [toWorld, zoomAt]);
    useEffect(() => {
        const el = wrapRef.current;
        if(!el) return;
        el.addEventListener('wheel', onWheel, {passive: false});
        return () => el.removeEventListener('wheel', onWheel);
    }, [onWheel]);

    // slider value 0..100 (log scale between maxW=0 and minW=100)
    const sliderVal = useMemo(() => {
        const t = Math.log(maxW / vb.w) / Math.log(maxW / minW);
        return Math.round(Math.max(0, Math.min(1, t)) * 100);
    }, [vb.w, minW, maxW]);
    const setSliderVal = (val) => {
        const t = Math.max(0, Math.min(100, val)) / 100;
        const nw = maxW / Math.pow(maxW / minW, t);
        setVb((v) => {
            const cx = v.x + v.w / 2, cy = v.y + v.h / 2;
            const nh = nw * (v.h / v.w);
            return {x: cx - nw / 2, y: cy - nh / 2, w: nw, h: nh};
        });
    };
    const zoomStep = (dir) => {
        setVb((v) => {
            const nw = Math.min(maxW, Math.max(minW, v.w * (dir > 0 ? 1 / 1.35 : 1.35)));
            const cx = v.x + v.w / 2, cy = v.y + v.h / 2;
            const nh = nw * (v.h / v.w);
            return {x: cx - nw / 2, y: cy - nh / 2, w: nw, h: nh};
        });
    };

    // ---- pointer interactions ------------------------------------------------
    // STUDIO rules: plain drag on empty canvas PANS (mouse + one-finger touch),
    // two fingers pinch-zoom. LEGACY rules: drag starting ON a seat box-selects
    // (replace; Shift adds); click toggles a seat; sold seats never select.
    const drag = useRef(null);
    const pointers = useRef(new Map());  // pointerId -> {x, y}
    const pinch = useRef(null);
    const suppressClick = useRef(0); // timestamp of the last gesture end
    const lastPointerType = useRef('mouse');
    const [box, setBox] = useState(null);
    const [space, setSpace] = useState(false);
    useEffect(() => {
        const d = (e) => { if(e.code === 'Space') setSpace(true); };
        const u = (e) => { if(e.code === 'Space') setSpace(false); };
        window.addEventListener('keydown', d); window.addEventListener('keyup', u);
        return () => { window.removeEventListener('keydown', d); window.removeEventListener('keyup', u); };
    }, []);

    // cursor by context: what would a press RIGHT HERE do? seat → click
    // (pointer) · empty space → pan (grab / grabbing while dragging) ·
    // Shift → box-select (+ crosshair), space → forced pan.
    const [shiftKey, setShiftKey] = useState(false);
    useEffect(() => {
        const d = (e) => { if(e.key === 'Shift') setShiftKey(true); };
        const u = (e) => { if(e.key === 'Shift') setShiftKey(false); };
        window.addEventListener('keydown', d); window.addEventListener('keyup', u);
        return () => { window.removeEventListener('keydown', d); window.removeEventListener('keyup', u); };
    }, []);
    const [cursor, setCursor] = useState('grab');
    const cursorRef = useRef('grab');
    const setCursorIf = (c) => { if(cursorRef.current !== c) { cursorRef.current = c; setCursor(c); } };

    const seatAt = (w, maxDist) => {
        let best = null, bd = (maxDist ?? r * 1.6) ** 2;
        for(const s of seats) {
            const dx = s.x - w.x, dy = s.y - w.y, d = dx * dx + dy * dy;
            if(d < bd) { bd = d; best = s; }
        }
        return best;
    };

    const onPointerDown = (e) => {
        if(e.pointerType === 'mouse' && e.button !== 0) return;
        cancelFocusTween(); focusPrevVb.current = null;
        lastPointerType.current = e.pointerType || 'mouse';
        svgRef.current.setPointerCapture(e.pointerId);
        pointers.current.set(e.pointerId, {x: e.clientX, y: e.clientY});
        if(pointers.current.size === 2) {
            // second finger → pinch zoom; whatever drag was running is over
            const [a, b] = [...pointers.current.values()];
            pinch.current = {
                d0: Math.hypot(a.x - b.x, a.y - b.y) || 1,
                mid0: toWorld((a.x + b.x) / 2, (a.y + b.y) / 2),
                vb0: vb
            };
            drag.current = null;
            setBox(null);
            return;
        }
        const rect = svgRef.current.getBoundingClientRect();
        const scale = Math.min(rect.width / vb.w, rect.height / vb.h);
        const w = toWorld(e.clientX, e.clientY);
        // mouse drag starting on a seat = box select (legacy); anything else
        // pans — EXCEPT Shift+drag, which is ALWAYS box-select (additive),
        // even when it starts on empty space
        const onSeat = e.pointerType === 'mouse' && !space && seatAt(w, r * 1.8);
        const shiftSelect = e.pointerType === 'mouse' && e.shiftKey && !space;
        drag.current = {
            startX: e.clientX, startY: e.clientY, wx: w.x, wy: w.y,
            shift: e.shiftKey, moved: false,
            pan: (!onSeat && !shiftSelect) || space, vb, scale
        };
    };
    const onPointerMove = (e) => {
        const pt = pointers.current.get(e.pointerId);
        if(pt) { pt.x = e.clientX; pt.y = e.clientY; }
        if(pinch.current && pointers.current.size >= 2) {
            const [a, b] = [...pointers.current.values()];
            const p = pinch.current;
            const factor = (Math.hypot(a.x - b.x, a.y - b.y) || 1) / p.d0;
            const nw = Math.min(maxW, Math.max(minW, p.vb0.w / factor));
            const nh = nw * (p.vb0.h / p.vb0.w);
            // keep the world point that was under the original midpoint under
            // the CURRENT midpoint (pan + zoom in one move)
            const rect = svgRef.current.getBoundingClientRect();
            const scale = Math.min(rect.width / nw, rect.height / nh);
            const offX = (rect.width - nw * scale) / 2;
            const offY = (rect.height - nh * scale) / 2;
            const midX = (a.x + b.x) / 2, midY = (a.y + b.y) / 2;
            setVb({
                x: p.mid0.x - (midX - rect.left - offX) / scale,
                y: p.mid0.y - (midY - rect.top - offY) / scale,
                w: nw, h: nh
            });
            return;
        }
        const d = drag.current;
        if(!d) {
            // idle hover — pick the cursor for what a press here would do
            if(e.pointerType === 'mouse') {
                if(e.shiftKey && !space) setCursorIf('crosshair');
                else if(space) setCursorIf('grab');
                else setCursorIf(seatAt(toWorld(e.clientX, e.clientY), r * 1.8) ? 'pointer' : 'grab');
            }
            return;
        }
        if(Math.abs(e.clientX - d.startX) + Math.abs(e.clientY - d.startY) > 4) d.moved = true;
        if(d.pan) {
            if(d.moved) setCursorIf('grabbing');
            const dx = (e.clientX - d.startX) / d.scale;
            const dy = (e.clientY - d.startY) / d.scale;
            setVb({...d.vb, x: d.vb.x - dx, y: d.vb.y - dy});
        } else if(d.moved) {
            setCursorIf('crosshair');
            const w = toWorld(e.clientX, e.clientY);
            setBox({x: Math.min(d.wx, w.x), y: Math.min(d.wy, w.y), w: Math.abs(w.x - d.wx), h: Math.abs(w.y - d.wy)});
        }
    };
    const onPointerUp = (e) => {
        pointers.current.delete(e.pointerId);
        try { svgRef.current.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
        if(pinch.current) {
            if(pointers.current.size < 2) pinch.current = null;
            suppressClick.current = Date.now();
            drag.current = null;
            return;
        }
        const d = drag.current; drag.current = null;
        if(!d) return;
        if(d.pan) { setBox(null); if(d.moved) suppressClick.current = Date.now(); return; }
        if(!d.moved) { setBox(null); return; } // a click — handled by onSeatClick
        suppressClick.current = Date.now();
        const w = toWorld(e.clientX, e.clientY);
        const x0 = Math.min(d.wx, w.x), x1 = Math.max(d.wx, w.x);
        const y0 = Math.min(d.wy, w.y), y1 = Math.max(d.wy, w.y);
        const hit = [];
        for(const s of seats) if(selectable(s) && s.x >= x0 && s.x <= x1 && s.y >= y0 && s.y <= y1) hit.push(s.code);
        // LEGACY: box replaces the selection; Shift+box adds to it
        setSelected((prev) => {
            const next = d.shift ? new Set(prev) : new Set();
            hit.forEach((c) => next.add(c));
            return next;
        });
        setBox(null);
    };
    const onPointerCancel = (e) => {
        pointers.current.delete(e.pointerId);
        if(pointers.current.size < 2) pinch.current = null;
        drag.current = null;
        setBox(null);
    };

    // setPointerCapture retargets click/dblclick to the <svg> itself, so
    // e.target never carries data-row/data-code — resolve the REAL element
    // under the pointer instead.
    const elAt = (e) => (document.elementFromPoint ? document.elementFromPoint(e.clientX, e.clientY) : e.target) || e.target;

    const onSeatClick = (e) => {
        if(Date.now() - suppressClick.current < 400) { suppressClick.current = 0; return; }
        const target = elAt(e);
        // a click on a row-number label selects the whole row (legacy)
        const rowKey = target.getAttribute && target.getAttribute('data-row');
        if(rowKey) {
            e.stopPropagation();
            selectRowByKey(rowKey, e.shiftKey);
            return;
        }
        const code = target.getAttribute && target.getAttribute('data-code');
        const s = code ? seatByCode.get(code) : seatAt(toWorld(e.clientX, e.clientY), r * (lastPointerType.current === 'touch' ? 2.2 : 1.4));
        if(!s) return;
        e.stopPropagation();
        // LEGACY: a sold seat never joins the selection — clicking it opens the
        // order-details popup instead.
        if(s.state === 'sold') { onSoldClick && onSoldClick(s, {x: e.clientX, y: e.clientY}); return; }
        // LEGACY: a reserved seat that is ALREADY selected shows its reservation
        // popup on the next click.
        if(s.state === 'reserved' && selected.has(s.code)) {
            onReservedClick && onReservedClick(s, {x: e.clientX, y: e.clientY});
            return;
        }
        setSelected((prev) => {
            const next = new Set(prev);
            if(next.has(s.code)) next.delete(s.code); else next.add(s.code);
            return next;
        });
    };

    // double-click a seat → select its contiguous BLOCK in the row: expand in
    // both directions until an aisle/space. (Whole-row select stays on the
    // row-number label click.) Shift adds to the existing selection.
    const onSeatDblClick = (e) => {
        const target = elAt(e);
        const code = target.getAttribute && target.getAttribute('data-code');
        const s = code && seatByCode.has(code)
            ? seatByCode.get(code)
            : seatAt(toWorld(e.clientX, e.clientY), r * (lastPointerType.current === 'touch' ? 2.2 : 1.4));
        if(!s) return;
        e.stopPropagation();
        if(s.state === 'sold') return;
        const g = rowGroups.find((x) => x.key === s.smaid + '|' + s.row);
        let runCodes = [s.code];
        if(g && g.seats.length > 1) {
            const ss = g.seats;
            const idx = ss.findIndex((x) => x.code === s.code);
            if(idx >= 0) {
                const dists = [];
                for(let i = 1; i < ss.length; i++) dists.push(Math.hypot(ss[i].x - ss[i - 1].x, ss[i].y - ss[i - 1].y));
                const med = [...dists].sort((a, b) => a - b)[Math.floor(dists.length / 2)] || 0;
                let lo = idx, hi = idx;
                while(lo > 0 && dists[lo - 1] <= med * 1.7) lo--;
                while(hi < ss.length - 1 && dists[hi] <= med * 1.7) hi++;
                runCodes = ss.slice(lo, hi + 1).filter(selectable).map((x) => x.code);
            }
        }
        setSelected((prev) => {
            const next = e.shiftKey ? new Set(prev) : new Set();
            runCodes.forEach((c) => next.add(c));
            return next;
        });
    };

    const selArr = useMemo(() => Array.from(selected).map((c) => seatByCode.get(c)).filter(Boolean), [selected, seatByCode]);
    const hlArr = useMemo(() => (highlightCodes || []).map((c) => seatByCode.get(c)).filter(Boolean), [highlightCodes, seatByCode]);

    return (
        <css.canvasWrap ref={wrapRef}
            style={{cursor: space ? (cursor === 'grabbing' ? 'grabbing' : 'grab') : (shiftKey ? 'crosshair' : cursor)}}>
            <svg ref={svgRef} width="100%" height="100%"
                viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
                preserveAspectRatio="xMidYMid meet"
                onPointerDown={onPointerDown} onPointerMove={onPointerMove}
                onPointerUp={onPointerUp} onPointerCancel={onPointerCancel}
                onClickCapture={onSeatClick} onDoubleClickCapture={onSeatDblClick}>
                {/* selection halo UNDER the seats (legacy light-blue con background) */}
                <g pointerEvents="none">
                    {selArr.map((s) => (
                        <circle key={'sb' + s.code} cx={s.x} cy={s.y} r={r * 1.52}
                            fill={SELECTED_BG} />
                    ))}
                </g>
                <SeatLayer seats={seats} r={r} dataVersion={dataVersion} ticketColors={ticketColors} />
                {/* row numbers — widget-parity positions (ends + aisle gaps), click = select row */}
                {showRowLabels &&
                    <g fill="#334155" fontWeight="700" fontFamily="inherit"
                        stroke="#f8fafc" strokeWidth={rowFont * 0.16} paintOrder="stroke" style={{userSelect: 'none'}}>
                        {rowLabelPts.map((l) => (
                            <text key={l.key} data-row={l.rowKey} x={l.x} y={l.y} fontSize={rowFont}
                                textAnchor="middle" dominantBaseline="central"
                                style={{cursor: 'pointer'}}>
                                <title>לחץ כאן לבחירת שורה</title>
                                {l.row}
                            </text>
                        ))}
                    </g>}
                {/* state icons (sold armchair / burned flame / reserved handshake / blocked eye) */}
                <StateIconLayer stateSeats={stateSeats} r={r} badge={showSeatNums} dataVersion={dataVersion} />
                {/* seat numbers — ALWAYS rendered when legible, sold seats included */}
                {showSeatNums &&
                    <g pointerEvents="none" fontWeight="700" style={{userSelect: 'none'}}>
                        {visibleSeats.map((s) => {
                            if(s.seat == null || s.seat === '' || s.state === 'hidden') return null;
                            return (
                                <text key={'sn' + s.code} x={s.x} y={s.y}
                                    fill={numColor(s, ticketColors)}
                                    fontSize={r * Math.min(1.05, 1.8 / Math.max(1, String(s.seat).length))}
                                    textAnchor="middle" dominantBaseline="central">{s.seat}</text>
                            );
                        })}
                    </g>}
                {/* highlight (a ticket's seats / hovered gush) — amber halo */}
                <g pointerEvents="none">
                    {hlArr.map((s) => (
                        <circle key={s.code} cx={s.x} cy={s.y} r={r * 1.38}
                            fill="none" stroke="#f59e0b" strokeWidth={r * 0.35} />
                    ))}
                </g>
                {/* selection ring (legacy #01b4fc) */}
                <g pointerEvents="none">
                    {selArr.map((s) => (
                        <circle key={s.code} cx={s.x} cy={s.y} r={r * 1.52}
                            fill="none" stroke={SELECT_STROKE} strokeWidth={r * 0.28} />
                    ))}
                </g>
                {/* section (gush) labels */}
                <g>
                    {centroids.map((c) => {
                        const title = areaTitle[c.smaid] || '';
                        const fs = vb.w * 0.014;
                        const w = Math.max(fs * 2.4, title.length * fs * 0.62 + fs);
                        const h = fs * 1.7;
                        return (
                            <g key={c.smaid} style={{cursor: 'pointer'}}
                                onClick={(e) => { e.stopPropagation(); if(onSelectArea) onSelectArea(c.smaid, e.shiftKey); }}
                                onMouseEnter={() => onHoverArea && onHoverArea(c.smaid)}
                                onMouseLeave={() => onHoverArea && onHoverArea(null)}>
                                <rect x={c.x - w / 2} y={c.y - h / 2} width={w} height={h} rx={h * 0.35}
                                    fill="#0f172a" fillOpacity={0.66} stroke="#ffffff" strokeOpacity={0.25} strokeWidth={fs * 0.05} />
                                <text x={c.x} y={c.y} fontSize={fs} fill="#fff" fontWeight="700"
                                    textAnchor="middle" dominantBaseline="central" style={{userSelect: 'none'}}>{title}</text>
                            </g>
                        );
                    })}
                </g>
                {box && <rect x={box.x} y={box.y} width={box.w} height={box.h}
                    fill="rgba(0,172,241,0.22)" stroke="#ffffff" strokeWidth={r * 0.2} pointerEvents="none" />}
            </svg>
            {/* zoom slider (legacy .slider-zoon-m — הגדל / הקטן) */}
            <css.zoomSlider>
                <button title="הגדל" onClick={() => zoomStep(1)}>+</button>
                <input type="range" min="0" max="100" value={sliderVal}
                    onChange={(e) => setSliderVal(Number(e.target.value))}
                    aria-label="זום" />
                <button title="הקטן" onClick={() => zoomStep(-1)}>−</button>
            </css.zoomSlider>
        </css.canvasWrap>
    );
}

export default SeatCanvas;
